import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
} from '@mui/material';
import { Visibility, VisibilityOff, HowToReg as RegisterIcon, Login as LoginIcon } from '@mui/icons-material';
import { fetchApiWithoutToken } from '../services/api';
import { generateHMAC } from '../utils/crypto';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Register = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  // Company registration state
  const [companyName, setCompanyName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPhone, setAdminPhone] = useState('');
  const [companyregNo, setCompanyregNo] = useState('');

  // Employee registration state
  const [companyCode, setCompanyCode] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [employeePhone, setEmployeePhone] = useState('');
  const [employeeEmail, setEmployeeEmail] = useState('');
  const [employeePassword, setEmployeePassword] = useState('');

  // 사업자등록번호 유효성 검사 함수
  const validateBusinessNumber = (businessNumber: string) => {
    // 숫자만 추출
    const cleanNo = businessNumber.replace(/[^\d]/g, '');

    // 길이가 10자리가 아니면 false
    if (cleanNo.length !== 10) return false;

    // 가중치 배열
    const checkArr = [1, 3, 7, 1, 3, 7, 1, 3, 5];
    let sum = 0;

    // 1~9번째 자리까지 가중치를 곱하여 합산
    for (let i = 0; i < 9; i++) {
      sum += Number(cleanNo[i]) * checkArr[i];
    }

    // 10번째 자리의 검증코드 계산
    sum += Math.floor((Number(cleanNo[8]) * 5) / 10);
    const checkDigit = (10 - (sum % 10)) % 10;

    // 계산된 검증코드와 실제 검증코드 비교
    return checkDigit === Number(cleanNo[9]);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
  };

  const handleCompanyRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 사업자등록번호 유효성 검사
    if (!validateBusinessNumber(companyregNo)) {
      setSnackbarMessage('사업자등록번호가 올바르지 않습니다');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);

    try {
      // 비밀번호 암호화
      const encryptedPassword = await generateHMAC(adminPassword);

      const postData = [
        {
          컬럼명: '@업체명',
          값: companyName,
        },
        {
          컬럼명: '@한글명',
          값: adminName,
        },
        {
          컬럼명: '@아이디',
          값: adminEmail,
        },
        {
          컬럼명: '@암호',
          값: encryptedPassword,
        },
        {
          컬럼명: '@휴대폰번호',
          값: adminPhone,
        },
        {
          컬럼명: '@종류',
          값: '업체등록',
        },
        // ,
        // {
        //   "컬럼명": "@사업자번호",
        //   "값": companyregNo
        // }
      ];

      const response = await fetchApiWithoutToken<any>('DBO.CW_T_회원가입', postData);

      if (response?.data?.table[0]?.회원번호 > 0) {
        setSnackbarMessage(`회사 등록이 완료되었습니다. 
          등록하신 관리자 email로 로그인 해주세요.`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        // 스낵바가 보일 수 있도록 1초 후 페이지 이동
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else if (response?.data?.table[0]?.회원번호 === -1) {
        setSnackbarMessage('중복된 이메일 주소입니다. 다시 확인해주세요.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(response.message || '회사 등록에 실패했습니다.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } 
    } catch (err) {
      setSnackbarMessage('회사 등록 중 오류가 발생했습니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // 비밀번호 암호화
      const encryptedPassword = await generateHMAC(employeePassword);

      const postData = [
        {
          컬럼명: '@업체명',
          값: companyCode,
        },
        {
          컬럼명: '@한글명',
          값: employeeName,
        },
        {
          컬럼명: '@아이디',
          값: employeeEmail,
        },
        {
          컬럼명: '@암호',
          값: encryptedPassword,
        },
        {
          컬럼명: '@종류',
          값: '직원등록',
        },
        {
          컬럼명: '@휴대폰번호',
          값: employeePhone,
        },
      ];

      const response = await fetchApiWithoutToken<any>('DBO.CW_T_회원가입', postData);

      if (response?.data?.table[0]?.회원번호 > 0) {
        setSnackbarMessage(`직원 등록에 완료되었습니다. 
          등록하신 직원 email로 로그인 해주세요.`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        // 스낵바가 보일 수 있도록 1초 후 페이지 이동
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else if (response?.data?.table[0]?.회원번호 === -1) {
        setSnackbarMessage('중복된 이메일 주소입니다. 다시 확인해주세요.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else if (response?.data?.table[0]?.회원번호 === -2) {
        setSnackbarMessage('업체코드가 잘못되었습니다. 확인하고 다시 입력해주세요');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(response.message || '직원 등록에 실패했습니다.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage('직원 등록 중 오류가 발생했습니다.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
      }}>
      {/* Left side - Register Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: '500px',
          }}>
          <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 'bold' }}>
            회원가입
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            회원가입 후 랜드박스의 모든 서비스를 이용하실 수 있습니다.
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="registration tabs">
              <Tab label="업체 회원가입" />
              <Tab label="직원 회원가입" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Box component="form" onSubmit={handleCompanyRegister}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="업체명"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="관리자 이름"
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="관리자 휴대폰번호"
                type="tel"
                value={adminPhone}
                onChange={(e) => setAdminPhone(e.target.value)}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="관리자이메일"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="비밀번호"
                type={showPassword ? 'text' : 'password'}
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="사업자등록번호"
                value={companyregNo}
                onChange={(e) => setCompanyregNo(e.target.value)}
                disabled={loading}
                helperText="'-' 없이 10자리 숫자로 입력해주세요"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5, bgcolor: '#4B6BFB' }}
                disabled={loading}
                startIcon={<RegisterIcon />}>
                {loading ? '처리중...' : '업체 회원가입'}
              </Button>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box component="form" onSubmit={handleEmployeeRegister}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="업체코드"
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value)}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="이름"
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                disabled={loading}
              />
                            <TextField
                margin="normal"
                required
                fullWidth
                label="휴대폰번호"
                value={employeePhone}
                onChange={(e) => setEmployeePhone(e.target.value)}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="이메일"
                type="email"
                value={employeeEmail}
                onChange={(e) => setEmployeeEmail(e.target.value)}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="비밀번호"
                type={showPassword ? 'text' : 'password'}
                value={employeePassword}
                onChange={(e) => setEmployeePassword(e.target.value)}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5, bgcolor: '#4B6BFB' }}
                disabled={loading}
                startIcon={<RegisterIcon />}>
                {loading ? '처리중...' : '직원 회원가입'}
              </Button>
            </Box>
          </TabPanel>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button onClick={() => navigate('/')} startIcon={<LoginIcon />} sx={{ color: 'text.secondary' }}>
              로그인 페이지로 이동
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Right side - Image */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          bgcolor: '#4B6BFB',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <Box
            component="img"
            src="https://kr.object.iwinv.kr/orgin/1/Place/20240112095134_GettyImages-1360401313.jpg"
            alt="Welcome illustration"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              // bgcolor: 'rgba(75, 107, 251, 0.35)',
              color: 'white',
              textAlign: 'center',
              p: 4,
            }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
              Welcome!
            </Typography>
            <Typography variant="h6">Create your account to get started.</Typography>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            px: 7,
            py: 3,
            mt: 22,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '& .MuiAlert-icon': {
              color: 'primary.contrastText',
            },
            '& .MuiAlert-message': {
              fontSize: '18px', // Alert 메시지의 폰트 크기
            },
          }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Register;
